import React, {CSSProperties, FC, memo, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {VisualCellActivityTypesEnum} from '../../../../../../libs/inboxes/business/src/cells/cellActivity';
import {assertUnreachable} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {MessageModel} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UnreadStatusesEnum} from '../../../../../helpers/models/conversationHelpers';
import {
  ActivityModel,
  ActivityVisualTypesEnum,
  BasicActivityTypesEnum,
} from '../../../../../models/api/activityModel';
import {
  ConversationHighlightModel,
  highlightWithPrependedText,
  SearchActivityTypesEnum,
} from '../../../../../models/api/conversationHighlightModel';
import {DraftSummaryModel} from '../../../../../models/api/draftSummaryModel';
import {
  BumpCommentModel,
  CommentDraftSummary,
  LastCommentDraftModel,
  LastCommentModel,
} from '../../../../../store/data/conversations/conversationCellSelectors';
import {VisualSearchResultCommentSummary} from './cellActivity';
import {findMostRecentActivityForCell} from './cellActivityCommon';
import {CellComment, renderSummary} from './cellComment';
import {CellCommentDraftSummary} from './cellCommentDraftSummary';
import {CellCommentingPrecog} from './cellCommentingPrecog';
import {CellCommentLine} from './cellCommentLine';
import {CellConversationShared} from './cellConversationShared';
import {CellReplyingPrecog} from './cellReplyingPrecog';
import {CellRuleNotify} from './cellRuleNotify';
import {
  CellSearchActivitySummaryHighlight,
  VisualSearchActivityModel,
} from './cellSearchActivitySummaryHighlight';
import {CellSummary} from './cellSummary';

/*
 * Constants.
 */

// En dash.
const sep = `– `;

/*
 * Props.
 */

interface CompactCellActivityProps {
  mentionableAliases?: ReadonlySet<string>;
  summary: string;
  commentingTeammate?: TeammateModel;
  replyingTeammate?: TeammateModel;
  draftSeedAuthors?: ReadonlyArray<TeammateModel>;
  lastComment: LastCommentModel | undefined;
  lastCommentDraft: LastCommentDraftModel | undefined;
  lastManualMessage: MessageModel | undefined;
  isSelected?: boolean;
  draft: DraftSummaryModel | undefined;
  commentDraftSummary: CommentDraftSummary | undefined;
  bumpActivity?: ActivityModel;
  bumpComment?: BumpCommentModel;
  bumpTeammateSource?: TeammateModel;
  unreadStatus?: UnreadStatusesEnum;
  hasAutoShareDrafts: boolean;
  isInDraftsFolder: boolean;
  /** User ID or delegated teammate ID. */
  teammateId: number;

  // Search
  conversationHighlight?: ConversationHighlightModel;
  searchActivity?: VisualSearchActivityModel;
  searchResultCommentSummary?: VisualSearchResultCommentSummary;
  conversationId?: number;
}

/*
 * Style.
 */

interface StyleProps {
  $isSearchSummaryMatch: boolean;
  $hasPrecog: boolean;
}
const StyledDiv = styled.div<StyleProps>`
  height: 36px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  ${ellipsis()};

  flex-grow: 1;
  max-width: fit-content;

  ${(p) => addFlexShrinkStyles(p)}
`;

function addFlexShrinkStyles(p: StyleProps) {
  if (p.$hasPrecog) {
    return css`
      flex-shrink: 0;
    `;
  }

  return css`
    /* When searching, if we have a highlight on the summary: */
    /* Give the same shrink factor as the subject. See compactCellContentCommon.tsx. */
    flex-shrink: ${p.$isSearchSummaryMatch ? 1 : 2};
  `;
}

/*
 * Component.
 */

enum CompactCellActivityContentTypesEnum {
  REPLY_PRECOG = 'REPLY_PRECOG',
  COMMENT_PRECOG = 'COMMENT_PRECOG',
  FLUID_CONTENT = 'FLUID_CONTENT',
  TEXT_CONTENT = 'TEXT_CONTENT',
}

const flexBasisValues: Record<CompactCellActivityContentTypesEnum, CSSProperties['flexBasis']> = {
  [CompactCellActivityContentTypesEnum.COMMENT_PRECOG]: 'auto',
  [CompactCellActivityContentTypesEnum.REPLY_PRECOG]: 'auto',
  [CompactCellActivityContentTypesEnum.TEXT_CONTENT]: 13,
  [CompactCellActivityContentTypesEnum.FLUID_CONTENT]: 65,
};

type CompactCellActivityContent = null | {type: CompactCellActivityContentTypesEnum; node: ReactNode};

export const CompactCellActivity: FC<CompactCellActivityProps> = memo((props) => {
  const content =
    maybeRenderPrecog(props) ??
    maybeRenderSearchHighlight(props) ??
    maybeRenderBumpActivity(props) ??
    maybeRenderMostRecentActivity(props) ??
    maybeRenderSearchResultComment(props) ??
    renderConversationSummary(props);

  if (!content) {
    return null;
  }

  const {conversationHighlight} = props;

  const isSearchSummaryMatch = Boolean(conversationHighlight?.attachments || conversationHighlight?.summary);
  const hasPrecog = content.type === CompactCellActivityContentTypesEnum.COMMENT_PRECOG;

  return (
    <StyledDiv
      $isSearchSummaryMatch={isSearchSummaryMatch}
      $hasPrecog={hasPrecog}
      style={{
        // When searching, if we have a highlight on the summary:
        // Split available space evenly between subject and summary. See compactCellContentCommon.tsx.
        flexBasis: isSearchSummaryMatch ? '50%' : flexBasisValues[content.type],
      }}
    >
      {content.node}
    </StyledDiv>
  );
});

function maybeRenderPrecog(props: CompactCellActivityProps): CompactCellActivityContent {
  const {isSelected} = props;

  // The replying precog always takes precedence.
  if (props.replyingTeammate) {
    return {
      type: CompactCellActivityContentTypesEnum.REPLY_PRECOG,
      node: <CellReplyingPrecog author={props.replyingTeammate} isSelected={isSelected} />,
    };
  }

  // After that comes the commenting precog.
  if (props.commentingTeammate) {
    return {
      type: CompactCellActivityContentTypesEnum.COMMENT_PRECOG,
      node: <CellCommentingPrecog author={props.commentingTeammate} isSelected={isSelected} />,
    };
  }

  return null;
}

function maybeRenderSearchHighlight(props: CompactCellActivityProps): CompactCellActivityContent {
  const {isSelected, searchActivity, conversationHighlight, mentionableAliases} = props;

  // If we are in search mode show the highlighted summary.
  if (conversationHighlight?.summary) {
    // As done for regular P2, prepend `sep` to summary.
    // We don't need to prepend `sep` to comment highlights because
    // the comment bubble provides enough visual separation.
    const isCommentSummaryHighlight =
      searchActivity?.visualType === SearchActivityTypesEnum.COMMENT && searchActivity?.author;

    const summaryHighlight = isCommentSummaryHighlight
      ? conversationHighlight.summary
      : highlightWithPrependedText(conversationHighlight.summary, sep);

    return {
      type: CompactCellActivityContentTypesEnum.TEXT_CONTENT,
      node: (
        <CellSearchActivitySummaryHighlight
          isSelected={isSelected}
          summaryHighlight={summaryHighlight}
          searchActivity={searchActivity}
          mentionableAliases={mentionableAliases}
          isCompact
        />
      ),
    };
  }

  // If we are in search mode show the attachment's filename.
  if (conversationHighlight?.attachments) {
    return {
      type: CompactCellActivityContentTypesEnum.TEXT_CONTENT,
      node: (
        <CellSummary
          isSelected={isSelected}
          summary={conversationHighlight?.attachments.text}
          highlight={conversationHighlight.attachments}
          type={searchActivity?.visualType}
          attachmentContentType={searchActivity?.visualContentType}
        />
      ),
    };
  }

  return null;
}

function maybeRenderBumpActivity(props: CompactCellActivityProps): CompactCellActivityContent {
  const {bumpActivity, bumpComment, bumpTeammateSource, isSelected, unreadStatus, mentionableAliases} = props;

  // If we have a bump comment, render it.
  if (bumpComment) {
    return {
      type: CompactCellActivityContentTypesEnum.FLUID_CONTENT,
      node: (
        <CellComment
          comment={bumpComment.comment}
          author={bumpComment.author}
          hasFollowingComments={!bumpComment.isLastComment}
          mentionableAliases={mentionableAliases}
          isSelected={isSelected}
          isUnread={unreadStatus === UnreadStatusesEnum.MESSAGE_UNREAD}
        />
      ),
    };
  }

  // If we have a basic bump activity, render it.
  if (bumpActivity && bumpActivity.visualType === ActivityVisualTypesEnum.BASIC) {
    // If we have a tracker_share, render it
    if (bumpActivity.type === BasicActivityTypesEnum.TRACKER_SHARE && bumpTeammateSource) {
      return {
        type: CompactCellActivityContentTypesEnum.FLUID_CONTENT,
        node: <CellConversationShared source={bumpTeammateSource} isSelected={isSelected} />,
      };
    }

    // If we have a notification from a rule, render it.
    if (bumpActivity.type === BasicActivityTypesEnum.RULE_NOTIFY) {
      return {
        type: CompactCellActivityContentTypesEnum.FLUID_CONTENT,
        node: <CellRuleNotify isSelected={isSelected} />,
      };
    }
  }

  return null;
}

function maybeRenderMostRecentActivity(props: CompactCellActivityProps): CompactCellActivityContent {
  const {isSelected, unreadStatus, mentionableAliases} = props;
  const mostRecentActivity = findMostRecentActivityForCell(props);

  switch (mostRecentActivity?.type) {
    case VisualCellActivityTypesEnum.TIMELINE_COMMENT: {
      return {
        type: CompactCellActivityContentTypesEnum.FLUID_CONTENT,
        node: (
          <CellComment
            author={mostRecentActivity.author}
            comment={mostRecentActivity.comment}
            mentionableAliases={mentionableAliases}
            isSelected={isSelected}
            isUnread={unreadStatus === UnreadStatusesEnum.MESSAGE_UNREAD}
          />
        ),
      };
    }

    case VisualCellActivityTypesEnum.TIMELINE_MESSAGE: {
      const {blurb} = mostRecentActivity.message;
      return {
        type: CompactCellActivityContentTypesEnum.TEXT_CONTENT,
        node: <CellSummary isSelected={isSelected} summary={`${sep}${blurb}`} />,
      };
    }

    case VisualCellActivityTypesEnum.DRAFT_COMMENT: {
      // TODO: Render the comment summary and full comment cases the same.
      if (`blurb` in mostRecentActivity.draft) {
        return {
          type: CompactCellActivityContentTypesEnum.FLUID_CONTENT,
          node: (
            <CellCommentDraftSummary
              summary={mostRecentActivity.draft}
              author={mostRecentActivity.author}
              mentionableAliases={mentionableAliases}
              isSelected={isSelected}
              isUnread={unreadStatus === UnreadStatusesEnum.MESSAGE_UNREAD}
              isCompact
            />
          ),
        };
      }

      return {
        type: CompactCellActivityContentTypesEnum.FLUID_CONTENT,
        node: (
          <CellComment
            isCompact
            comment={mostRecentActivity.draft}
            author={mostRecentActivity.author}
            mentionableAliases={mentionableAliases}
            isSelected={isSelected}
            isUnread={unreadStatus === UnreadStatusesEnum.MESSAGE_UNREAD}
          />
        ),
      };
    }

    // Drafts are not shown in the compact view.
    case VisualCellActivityTypesEnum.DRAFT_MESSAGE:
    case undefined:
      return null;

    default:
      assertUnreachable(mostRecentActivity);
      return null;
  }
}

function maybeRenderSearchResultComment(props: CompactCellActivityProps): CompactCellActivityContent {
  const {isSelected, searchResultCommentSummary, mentionableAliases} = props;

  if (searchResultCommentSummary) {
    return {
      type: CompactCellActivityContentTypesEnum.FLUID_CONTENT,
      node: (
        <CellCommentLine
          isSelected={isSelected}
          text={searchResultCommentSummary.text}
          author={searchResultCommentSummary.author}
          annotation={undefined}
          renderSummary={renderSummary}
          shouldDisplayAttachmentIcon={false}
          shouldUseEllipsisOverflow={false}
          mentionableAliases={mentionableAliases}
          isCompact
        />
      ),
    };
  }

  return null;
}

function renderConversationSummary(props: CompactCellActivityProps): CompactCellActivityContent {
  const {isSelected} = props;

  // Default to rendering the conversation summary.
  return {
    type: CompactCellActivityContentTypesEnum.TEXT_CONTENT,
    node: <CellSummary isSelected={isSelected} summary={`${sep}${props.summary}`} />,
  };
}
